import {WriteButton} from '../../common/buttons/WriteActions';
import {useState} from 'react';
import {Box, Card, CardContent, Chip, Collapse, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, Skeleton, Stack, Table, TableBody, TableCell, TableRow, TableSortLabel, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';



import {recipeAPI} from '../../../services/RecipeServices';
import {fillingAPI} from '../../../services/FillingServices';
import {decorationAPI} from '../../../services/DecorationService';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useNavigate} from 'react-router-dom';
import {useCurrency} from '../../../hooks/useCurrency';
import {RECIPE_SIZE} from '@mybake/ui/common/dictionary';
import {getIngredientAmount, getIngredientsTotalWeight} from './ingredientAmount';
import {AmountValue} from './AmountValue';
import {ApiErrorFeedback} from '../../common/ApiErrorFeedback';

const DetailLoading = () => <Stack spacing={1} sx={{p: 2}}><Skeleton variant="text" width="35%"/><Skeleton variant="rounded" height={80}/></Stack>;
const PriceValue = ({label, value}: {label: string; value?: number}) => {
    const {formatCurrency} = useCurrency();
    return <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>{label}: <Box component="span" color="text.primary" fontWeight={700}>{formatCurrency(value ?? 0)}</Box></Typography>;
};
const WeightValue = ({value}: {value: number}) => {
    const {t} = useAppTranslation();
    return <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>{t('items:weight')}: <Box component="span" color="text.primary" fontWeight={700}>{value.toFixed(2)} {t('items:grams_short')}</Box></Typography>;
};
const DescriptionText = ({value}: {value?: string}) => value?.trim()
    ? <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-wrap'}}>{value}</Typography>
    : null;

const ProportionSelect = ({value, onChange}: {value: string; onChange: (value: string) => void}) => {
    const {t} = useAppTranslation();
    return (
        <FormControl size="small" sx={{minWidth: 130}}>
            <InputLabel>{t('orders:recipe_proportion')}</InputLabel>
            <Select value={value} label={t('orders:recipe_proportion')} onChange={event => onChange(event.target.value)}>
                {RECIPE_SIZE.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </Select>
        </FormControl>
    );
};

const DetailTable = ({items, multiplier}: {items: Array<{id?: string | number; itemName?: string; name?: string; quantity?: number; weight?: number | null; proportions?: {proportion?: number | null; quantity?: number | null; weight?: number | null}[]; unitOfWeight?: string; unit?: string}>; multiplier: number}) => {
    return (
        <Table size="small">
            <TableBody>{items.map(item => {
                const unit = item.unitOfWeight ?? item.unit;
                const amount = getIngredientAmount({...item, unitOfWeight: unit}, multiplier);
                return <TableRow key={item.id ?? `${item.itemName ?? item.name}-${item.quantity}`}><TableCell>{item.itemName ?? item.name}</TableCell><TableCell align="right"><AmountValue quantity={amount.quantity} unit={unit}/></TableCell></TableRow>;
            })}</TableBody>
        </Table>
    );
};

export const RecipeInlineDetail = ({id, returnPage}: {id: string | number; returnPage?: number}) => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();
    const [proportion, setProportion] = useState('1.00');
    const [selectedFillingId, setSelectedFillingId] = useState('');
    const {data, isLoading, error} = recipeAPI.useGetRecipeV2Query(String(id), {skip: false});
    const {
        data: loadedFilling,
        isLoading: fillingLoading,
        error: fillingError,
    } = fillingAPI.useGetFillingV2Query(selectedFillingId, {skip: !selectedFillingId});

    if (isLoading) return <DetailLoading/>;
    if (error || !data) return error ? <ApiErrorFeedback error={error}/> : null;

    // RTK Query can retain the previous successful result when a skipped query is
    // selected. Gate it by the current selection so "not selected" clears the UI
    // and totals immediately, exactly like the legacy flow.
    const selectedFilling = selectedFillingId ? loadedFilling : undefined;
    const multiplier = Number(proportion);
    const totalPrice = (Number(data.price ?? 0) + Number(selectedFilling?.price ?? 0)) * multiplier;
    const recipeItems = (data.parts ?? []).flatMap(part => part.items ?? []);
    const totalWeight = getIngredientsTotalWeight(recipeItems, multiplier)
        + getIngredientsTotalWeight(selectedFilling?.items ?? [], multiplier);

    return (
        <Box sx={{p: {xs: 1, sm: 2}}}>
            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>{t('recipes:parts')}</Typography>
                <Stack direction="row" alignItems="center" justifyContent={{xs: 'space-between', sm: 'flex-end'}} spacing={2} flexWrap="wrap">
                    <ProportionSelect value={proportion} onChange={setProportion}/>
                    <PriceValue label={t('finance:price')} value={totalPrice}/>
                    <WeightValue value={totalWeight}/>
                    <WriteButton size="small" startIcon={<EditIcon/>} onClick={() => navigate(`${id}/edit`, {relative: 'path', state: {returnPage}})}>{t('common:edit')}</WriteButton>
                </Stack>
            </Stack>

            <DescriptionText value={data.recipeDescription}/>
            <Stack spacing={1} sx={{mt: 1}}>
                {(data.parts ?? []).map(part => (
                    <Card key={part.id} variant="outlined">
                        <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
                            <Typography fontWeight={700}>{part.partName || t('recipes:part')}</Typography>
                            <DescriptionText value={part.partDescription}/>
                            <Divider sx={{my: 1}}/>
                            <DetailTable items={part.items ?? []} multiplier={multiplier}/>
                        </CardContent>
                    </Card>
                ))}
                {(data.fillings ?? []).length > 0 && <Card variant="outlined">
                    <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
                        <Typography fontWeight={700} sx={{mb: 1}}>{t('recipes:fillings')}</Typography>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('recipes:filling')}</InputLabel>
                            <Select value={selectedFillingId} label={t('recipes:filling')} onChange={event => setSelectedFillingId(event.target.value)}>
                                <MenuItem value="">{t('recipes:not_selected')}</MenuItem>
                                {(data.fillings ?? []).map(filling => <MenuItem key={filling.id} value={filling.id}>{filling.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {fillingLoading && <Skeleton variant="rounded" height={72} sx={{mt: 1}}/>}
                        {fillingError && selectedFillingId && <Box sx={{mt: 1}}><ApiErrorFeedback error={fillingError}/></Box>}
                        {selectedFilling && <Box sx={{mt: 1}}>
                            <Typography fontWeight={700}>{selectedFilling.fillingName}</Typography>
                            <DescriptionText value={selectedFilling.fillingDescription}/>
                            <Divider sx={{my: 1}}/>
                            <DetailTable items={selectedFilling.items ?? []} multiplier={multiplier}/>
                        </Box>}
                    </CardContent>
                </Card>}
            </Stack>

        </Box>
    );
};

export const FillingInlineDetail = ({id, returnPage}: {id: string | number; returnPage?: number}) => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();
    const [proportion, setProportion] = useState('1.00');
    const {data, isLoading, error} = fillingAPI.useGetFillingV2Query(String(id), {skip: false});

    if (isLoading) return <DetailLoading/>;
    if (error || !data) return error ? <ApiErrorFeedback error={error}/> : null;

    const multiplier = Number(proportion);
    const totalWeight = getIngredientsTotalWeight(data.items ?? [], multiplier);
    return (
        <Box sx={{p: {xs: 1, sm: 2}}}>
            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>{t('recipes:ingredients')}</Typography>
                <Stack direction="row" alignItems="center" justifyContent={{xs: 'space-between', sm: 'flex-end'}} spacing={2} flexWrap="wrap">
                    <ProportionSelect value={proportion} onChange={setProportion}/>
                    <PriceValue label={t('finance:price')} value={Number(data.price ?? 0) * multiplier}/>
                    <WeightValue value={totalWeight}/>
                    <WriteButton size="small" startIcon={<EditIcon/>} onClick={() => navigate(`${id}/edit`, {relative: 'path', state: {returnPage}})}>{t('common:edit')}</WriteButton>
                </Stack>
            </Stack>
            <DescriptionText value={data.fillingDescription}/>
            <Box sx={{mt: 1}}><DetailTable items={data.items ?? []} multiplier={multiplier}/></Box>
            {(data.recipes ?? []).length > 0 && <Box sx={{mt: 1.5}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 0.75}}>{t('recipes:used_in_recipes')}:</Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {(data.recipes ?? []).map(recipe => <Chip key={recipe.recipeId} label={recipe.recipeName ?? recipe.recipeId} size="small"/>) }
                </Stack>
            </Box>}
        </Box>
    );
};

export const DecorationInlineDetail = ({id, returnPage}: {id: string | number; returnPage?: number}) => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();
    const {data, isLoading, error} = decorationAPI.useGetDecorationV2Query(String(id), {skip: false});
    if (isLoading) return <DetailLoading/>;
    if (error || !data) return error ? <ApiErrorFeedback error={error}/> : null;
    return (
        <Box sx={{p: {xs: 1, sm: 2}}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>{t('recipes:ingredients')}</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <PriceValue label={t('finance:price')} value={data.price}/>
                    <WriteButton size="small" startIcon={<EditIcon/>} onClick={() => navigate(`${id}/edit`, {relative: 'path', state: {returnPage}})}>{t('common:edit')}</WriteButton>
                </Stack>
            </Stack>
            <DescriptionText value={data.description}/>
            <Box sx={{mt: 1}}><DetailTable items={data.items ?? []} multiplier={1}/></Box>
        </Box>
    );
};

export const InlineRowShell = ({name, summary, expanded, onToggle, children}: {name?: string; summary?: React.ReactNode; expanded: boolean; onToggle: () => void; children: React.ReactNode}) => {
    const {t} = useAppTranslation();
    return (
        <Card variant="outlined" sx={{overflow: 'hidden', borderColor: expanded ? 'primary.main' : 'divider', boxShadow: expanded ? 2 : 0}}>
            <Box role="button" tabIndex={0} aria-expanded={expanded} onClick={onToggle} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onToggle(); } }} sx={{cursor: 'pointer', px: {xs: 1.25, sm: 1.75}, py: 1, minHeight: 52}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontWeight={800} noWrap sx={{minWidth: 0}}>{name}</Typography>
                    {summary && <Typography variant="body2" color="text.secondary" sx={{ml: 'auto!important', whiteSpace: 'nowrap'}}>{summary}</Typography>}
                    <IconButton size="small" aria-label={expanded ? t('recipes:collapse') : t('recipes:expand')} onClick={event => { event.stopPropagation(); onToggle(); }}><ExpandMoreIcon sx={{transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 180ms'}}/></IconButton>
                </Stack>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>{children}</Collapse>
        </Card>
    );
};

export const ListSort = ({active, descending, onChange, includePrice = true}: {active: 'name' | 'price'; descending: boolean; onChange: (field: 'name' | 'price') => void; includePrice?: boolean}) => {
    const {t} = useAppTranslation();
    const direction = descending ? 'desc' : 'asc';
    return (
        <Stack direction="row" spacing={2} alignItems="center" sx={{px: {xs: 1.25, sm: 1.75}, pb: 1, flexWrap: 'wrap'}}>
            <Typography variant="caption" color="text.secondary">{t('recipes:sort_by')}</Typography>
            <TableSortLabel active={active === 'name'} direction={active === 'name' ? direction : 'asc'} onClick={() => onChange('name')}>{t('recipes:name')}</TableSortLabel>
            {includePrice && <TableSortLabel active={active === 'price'} direction={active === 'price' ? direction : 'asc'} onClick={() => onChange('price')}>{t('finance:price')}</TableSortLabel>}
        </Stack>
    );
};
